import { AVAILABILITY_TEXT } from "../constants/availability.constants";
import { PricingVariant } from "../types/variant.types";

export interface PackageProductContext {
    customFields?: { quantityPerBox?: number | null } | null;
    customProductMappings?: { quantityPerBox?: number | null } | null;
}

export interface PackageVariant extends PricingVariant {
    pricing?: { fullPackageOnly?: boolean };
    quantityPerBox?: number | null;
}

export function getMoq(variant?: PricingVariant | null): number {
    return variant?.moq ?? 1;
}

export function getMinQ(variant?: PricingVariant | null): number {
    return variant?.pricingProduct?.minQ ?? getMoq(variant);
}

export function getMaxQ(variant?: PricingVariant | null): number | null {
    return variant?.pricingProduct?.maxQ ?? null;
}

export function getQuantityPerBox(
    variant?: PackageVariant | null,
    product?: PackageProductContext | null,
): number | null {
    const fromVariant = variant?.quantityPerBox;
    if (fromVariant != null && fromVariant > 0) {
        return fromVariant;
    }
    const fromCustomFields = product?.customFields?.quantityPerBox;
    if (fromCustomFields != null && fromCustomFields > 0) {
        return fromCustomFields;
    }
    const fromMappings = product?.customProductMappings?.quantityPerBox;
    if (fromMappings != null && Number(fromMappings) > 0) {
        return Number(fromMappings);
    }
    return null;
}

export function isFullPackageOnly(
    variant?: PackageVariant | null,
    supplierFullPackageOnly?: boolean,
): boolean {
    if (variant?.pricing?.fullPackageOnly) {
        return true;
    }
    return !!supplierFullPackageOnly;
}

export function getOrderStep(
    variant?: PackageVariant | null,
    product?: PackageProductContext | null,
    supplierFullPackageOnly?: boolean,
): number {
    if (!isFullPackageOnly(variant, supplierFullPackageOnly)) {
        return 1;
    }
    const quantityPerBox = getQuantityPerBox(variant, product);
    return quantityPerBox != null && quantityPerBox > 0 ? quantityPerBox : 1;
}

export function normalizePackageQuantity(
    quantity: number,
    variant?: PackageVariant | null,
    product?: PackageProductContext | null,
    supplierFullPackageOnly?: boolean,
): number {
    const minQ = getMinQ(variant);
    const maxQ = getMaxQ(variant);
    const step = getOrderStep(variant, product, supplierFullPackageOnly);
    let value = quantity;

    if (value < minQ) {
        value = minQ;
    }

    if (isFullPackageOnly(variant, supplierFullPackageOnly) && step > 1) {
        if (value % step !== 0) {
            value = Math.ceil(value / step) * step;
        }
        if (value < minQ) {
            value = Math.ceil(minQ / step) * step;
        }
    }

    if (maxQ != null && maxQ > 0 && value > maxQ) {
        if (isFullPackageOnly(variant, supplierFullPackageOnly) && step > 1) {
            value = Math.floor(maxQ / step) * step;
            if (value < minQ) {
                value = minQ;
            }
        } else {
            value = maxQ;
        }
    }

    return value;
}

export function getInitialQuantity(
    variant?: PackageVariant | null,
    product?: PackageProductContext | null,
    supplierFullPackageOnly?: boolean,
): number {
    return normalizePackageQuantity(
        getMinQ(variant),
        variant,
        product,
        supplierFullPackageOnly,
    );
}

export function getBundleLabel(
    variant?: PackageVariant | null,
    product?: PackageProductContext | null,
    supplierFullPackageOnly?: boolean,
): string | null {
    if (!isFullPackageOnly(variant, supplierFullPackageOnly)) {
        return null;
    }
    const quantityPerBox = getQuantityPerBox(variant, product);
    if (quantityPerBox == null || quantityPerBox <= 1) {
        return null;
    }
    return `Bundles of ${quantityPerBox}`;
}

export function getQuantityInfoText(variant?: PricingVariant | null): string {
    const minQ = getMinQ(variant);
    const maxQ = getMaxQ(variant);

    if (maxQ !== null && maxQ > 0) {
        return `${AVAILABILITY_TEXT.MIN_ORDER_PREFIX} ${minQ} ${AVAILABILITY_TEXT.QUANTITY_SEPARATOR} ${AVAILABILITY_TEXT.MAX_ORDER_PREFIX} ${maxQ}`;
    }
    return `${AVAILABILITY_TEXT.MIN_ORDER_PREFIX} ${minQ}`;
}

export function hasMinQ(variant?: PricingVariant | null): boolean {
    return !!(variant?.pricingProduct?.minQ);
}
